import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '../services/authService'
import type { User, AuthState, AuthStorageState } from '../types/auth'

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      
      login: async (email, password) => {
        set({ isLoading: true })
        try {
          const response = await authService.login(email, password)
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
            isLoading: false
          })
          
          // Set axios default header
          authService.setAuthToken(response.access_token)
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },
      
      loginWithToken: async (token) => {
        set({ isLoading: true })
        try {
          const response = await authService.loginWithToken(token)
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
            isLoading: false
          })
          authService.setAuthToken(response.access_token)
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },
      
      logout: async () => {
        try {
          await authService.logout()
        } catch (error) {
          console.error('Logout error:', error)
        } finally {
          get().clearAuth()
        }
      },
      
      setUser: (user, token) => {
        set({
          user,
          token,
          isAuthenticated: true
        })
        authService.setAuthToken(token)
      },
      
      refreshToken: async () => {
        try {
          const { token } = get()
          if (!token) {
            throw new Error('No token to refresh')
          }
          
          authService.setAuthToken(token)
          const response = await authService.refreshToken()
          
          set({
            token: response.access_token,
            isAuthenticated: true
          })
          authService.setAuthToken(response.access_token)
        } catch (error) {
          console.error('Token refresh failed:', error)
          get().clearAuth()
          throw error
        }
      },
      
      clearAuth: () => {
        authService.clearAuthToken()
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        })
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state): AuthStorageState => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)